import React, { useContext, useEffect, useState } from 'react';
import { Col } from 'react-dsgov';
import { Row } from '@govbr-ds/react-components';
import styles from './formNCMVendaNacional.module.scss';
import { BrButton, BrInput, BrLoading, BrSelect } from '@govbr-ds/react-components';
import { formatCurrency, removeFormatCurrency } from '../../../../../../../../../../../utils/formatCurrency';
import { DiefCfemContext } from '../../../../../../../../../../../contexts/DiefCfemContext';
import ErrorMessage from '../../../../../../../../../../../components/errorMessage/errorMessage';
import AtualizarInformacoes from '../../../../../../../../atualizarInformacoes/atualizarInformacoes';
import { useBackEndRequests } from '../../../../../../../../../lib/backEndRequests';
import { useNotificacao } from '../../../../../../../../../../../hooks/useNotificacao';
import { ITributos } from '../../../../../../../../../../../interfaces/ITributos';
import { useQuery } from '@tanstack/react-query';
import { ICodigoNcm } from '../../../../../../../../../../../interfaces/ICodigoNcm';
import {
  IInformacaoVendaNacionalEnviar,
  IVendaNacionalNCMEnviar,
  IVendaNacionalNCMReceber,
} from '../../../../../../../../../../../interfaces/IVendaNacional';
import { validarVendaNacional } from './formValidacoes/formNCMVendaNacionalValidacoes';
import { EPrecoUtilizadoVendaNacional } from '../../../../../../../../../../../enums/EPrecoUtilizadoVendaNacional';
import AnmSwitch from '../../../../../../../../../../../components/anmSwitch/anmSwitch';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import SecaoDeducoes from 'components/secaoDeducoes/secaoDeducoes';

interface IFormNCMVendaNacionalProps {
  refetchListaNCM: () => void;
  isEdicaoNcm: boolean;
  setIsEdicaoNcm?: React.Dispatch<React.SetStateAction<boolean>>;
  refetchDadosAnteriores?: () => void;
}

const FormNCMVendaNacional = ({ refetchListaNCM, isEdicaoNcm, setIsEdicaoNcm }: IFormNCMVendaNacionalProps) => {
  const [tributosAdicionados, setTributosAdicionados] = useState<
    {
      nome: string;
      valor: number;
    }[]
  >([]);

  const [receitaBruta, setReceitaBruta] = useState<string>('');
  const [quantidade, setQuantidade] = useState<string>('');
  const [precoCorrente, setPrecoCorrente] = useState<string>('');
  const [precoUtilizado, setPrecoUtilizado] = useState<{ nomeCampo: string; valor: number }>({
    nomeCampo: 'Preço Corrente ',
    valor: 0,
  });

  const [haCompraTerceiro, setHaCompraTerceiro] = React.useState<boolean>(false);
  const [valorTotalTerceiros, setValorTotalTerceiros] = useState<string>('');
  const [quantidadeTotalTerceiros, setQuantidadeTotalTerceiros] = useState<string>('');

  const [codigoNCMSelecionado, setCodigoNCMSelecionado] = useState<string>('');
  const [unidadeMedidaSelecionada, setUnidadeMedidaSelecionada] = useState<string>('');

  const [haRejeito, setHaRejeito] = React.useState<boolean>(false);
  const [haUsoEspecifico, setHaUsoEspecifico] = React.useState<boolean>(false);

  const [isLoadingInfo, setIsLoadingInfo] = React.useState<boolean>(false);
  const [loadingBotao, setLoadingBotao] = React.useState<boolean>(false);

  const [info, setInfo] = React.useState<{ label: string; value: number; precoUtilizado?: string }[]>([
    {
      label: 'Preço Utilizado (R$)',
      value: precoUtilizado.valor,
      precoUtilizado: precoUtilizado.nomeCampo,
    },
    { label: 'Deduções (R$)', value: 0 },
    { label: 'Base de Cálculo (R$)', value: 0 },
  ]);

  const {
    getTributosVendaNacional,
    getNCMVendaNacional,
    getUnidadesMedidaVendaNacional,
    postNCMVendaNacional,
    postReceberInformacoesVendaNacional,
    getNCMAdicionadoVendaNacional,
    putNCMVendaNacional,
  } = useBackEndRequests();

  const mostrarNotificacao = useNotificacao();
  const context = useContext(DiefCfemContext);

  const {
    data: tributosPossiveisData,
    isLoading: tributosPossiveisLoading,
    error: tributosPossiveisError,
    refetch: refetchTributosPossiveis,
  } = useQuery<ITributos, IErrorWithSentryId>({
    queryKey: ['getTributosVendaNacional'],
    queryFn: () => getTributosVendaNacional(context?.idOperacaoEditar.toString() || ''),
    refetchOnWindowFocus: false,
  });

  const {
    data: ncmData,
    isLoading: ncmLoading,
    error: ncmError,
    refetch: refetchNcm,
  } = useQuery<ICodigoNcm[], IErrorWithSentryId>({
    queryKey: ['getCodigoNCMVendaNacional'],
    queryFn: () => getNCMVendaNacional(),
    refetchOnWindowFocus: false,
    retryDelay: 100,
  });

  const {
    data: unidadeMedidaData,
    isLoading: unidadeMedidaLoading,
    error: unidadeMedidaError,
    refetch: refetchUnidadeMedida,
  } = useQuery<string[], IErrorWithSentryId>({
    queryKey: ['getUnidadesMedidaVendaNacional'],
    queryFn: () => getUnidadesMedidaVendaNacional(),
    refetchOnWindowFocus: false,
    retryDelay: 100,
  });

  const {
    data: ncmAdicionadoData,
    isLoading: ncmAdicionadoDataLoading,
    error: ncmAdicionadoDataError,
  } = useQuery<IVendaNacionalNCMReceber, IErrorWithSentryId>({
    queryKey: ['getNCMAdicionadoVendaNacional', context?.idVendaNacionalNCM],
    queryFn: () => getNCMAdicionadoVendaNacional(context?.idVendaNacionalNCM ?? -1),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: isEdicaoNcm && context?.idVendaNacionalNCM !== -1,
  });

  useEffect(() => {
    if (isEdicaoNcm && ncmAdicionadoData) {
      setCodigoNCMSelecionado(ncmAdicionadoData?.codigoNCM ?? '');
      setUnidadeMedidaSelecionada(ncmAdicionadoData?.unidadeMedida ?? '');
      setQuantidade(ncmAdicionadoData?.quantidade?.toString() ?? '');
      setReceitaBruta(formatCurrency(ncmAdicionadoData?.receitaBrutaVenda ?? 0));
      setPrecoCorrente(formatCurrency(ncmAdicionadoData?.precoCorrente ?? 0));
      setPrecoUtilizado({
        nomeCampo:
          ncmAdicionadoData?.precoPraticado === ncmAdicionadoData?.precoCorrente ? 'Preço Corrente' : 'Preço Praticado',
        valor: ncmAdicionadoData?.precoPraticado ?? 0,
      });
      setTributosAdicionados(ncmAdicionadoData?.tributosValores ?? []);
      setValorTotalTerceiros(formatCurrency(ncmAdicionadoData?.valorAquisicaoTerceiros ?? 0));
      setQuantidadeTotalTerceiros(ncmAdicionadoData?.quantidadeAquisicaoTerceiros?.toString() ?? '');
      setHaRejeito(ncmAdicionadoData?.isRejeito ?? false);
      setHaUsoEspecifico(ncmAdicionadoData?.hasUsoEspecifico ?? false);
    }
  }, [isEdicaoNcm, ncmAdicionadoData]);

  const limparCampos = () => {
    setCodigoNCMSelecionado('');
    setUnidadeMedidaSelecionada('');
    setQuantidade('');
    setReceitaBruta('');
    setPrecoCorrente('');
    setPrecoUtilizado({ nomeCampo: 'Preço Corrente', valor: 0 });
    setTributosAdicionados([]);
    setValorTotalTerceiros('');
    setQuantidadeTotalTerceiros('');
    setHaRejeito(false);
    setHaUsoEspecifico(false);
    setInfo([
      {
        label: 'Preço Utilizado (R$)',
        value: 0,
        precoUtilizado: 'Preço Corrente',
      },
      { label: 'Deduções (R$)', value: 0 },
      { label: 'Base de Cálculo (R$)', value: 0 },
    ]);
  };

  const navegarParaListaNCM = async () => {
    const buttonEdit = document.getElementById('lista-ncm-adicionado');

    if (buttonEdit) {
      window.scrollTo({
        top: buttonEdit.offsetTop,
      });
    }
  };

  const adicionarNCM = async () => {
    if (
      !validarVendaNacional(
        codigoNCMSelecionado,
        unidadeMedidaSelecionada,
        parseFloat(quantidade),
        receitaBruta,
        precoCorrente,
        tributosAdicionados,
        tributosPossiveisData,
        mostrarNotificacao,
        haCompraTerceiro,
        parseFloat(quantidadeTotalTerceiros),
        valorTotalTerceiros
      )
    )
      return false;

    const ncmData: IVendaNacionalNCMEnviar = {
      idVendaNacional: context?.idVendaNacional || -1,
      codigoNCM: codigoNCMSelecionado,
      unidadeMedida: unidadeMedidaSelecionada,
      quantidade: parseFloat(quantidade),
      receitaBrutaVenda: removeFormatCurrency(receitaBruta),
      precoCorrente: removeFormatCurrency(precoCorrente),
      precoPraticado: parseFloat(quantidade) ? removeFormatCurrency(receitaBruta) / parseFloat(quantidade) : 0,
      tributosValores: tributosAdicionados,
      valorAquisicaoTerceiros: haCompraTerceiro ? removeFormatCurrency(valorTotalTerceiros) : 0,
      quantidadeAquisicaoTerceiros: haCompraTerceiro ? parseFloat(quantidadeTotalTerceiros) : 0,
      isRejeito: haRejeito,
      hasUsoEspecifico: haUsoEspecifico,
      isBemMineralAdquiridoTerceiro: haCompraTerceiro,
    };

    setLoadingBotao(true);

    if (isEdicaoNcm) {
      const ncmDataComId = {
        ...ncmData,
        id: context?.idVendaNacionalNCM || -1,
      };
      await putNCMVendaNacional(ncmDataComId, context?.idVendaNacionalNCM || -1)
        .then(() => {
          limparCampos();
          refetchListaNCM();
          navegarParaListaNCM();
          if (setIsEdicaoNcm) setIsEdicaoNcm(false);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao salvar NCM, tente novamente.',
            ...(err && { complementoSentryId: err }),
          });
        })
        .finally(() => {
          setLoadingBotao(false);
        });
    } else {
      await postNCMVendaNacional(ncmData)
        .then(() => {
          limparCampos();
          refetchListaNCM();
          navegarParaListaNCM();
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao enviar NCM, tente novamente.',
            ...(err && { complementoSentryId: err }),
          });
        })
        .finally(() => {
          setLoadingBotao(false);
        });
    }
  };

  useEffect(() => {
    if (quantidade !== '' && parseFloat(quantidade) > 0) {
      let precoCalculado = removeFormatCurrency(receitaBruta) / parseFloat(quantidade);
      if (precoCalculado > removeFormatCurrency(precoCorrente))
        setPrecoUtilizado({ nomeCampo: 'Preço Praticado', valor: precoCalculado });
      else setPrecoUtilizado({ nomeCampo: 'Preço Corrente', valor: removeFormatCurrency(precoCorrente) });
    }
  }, [quantidade, receitaBruta, precoCorrente]);

  useEffect(() => {
    if (isEdicaoNcm && ncmAdicionadoData) {
      setCodigoNCMSelecionado(ncmAdicionadoData?.codigoNCM);
      setUnidadeMedidaSelecionada(ncmAdicionadoData?.unidadeMedida);
      setReceitaBruta(formatCurrency(ncmAdicionadoData?.receitaBrutaVenda));
      setQuantidade(ncmAdicionadoData?.quantidade.toString());
      setTributosAdicionados(ncmAdicionadoData?.tributosValores);
      setHaRejeito(ncmAdicionadoData?.isRejeito);
      setHaUsoEspecifico(ncmAdicionadoData?.hasUsoEspecifico);
      setHaCompraTerceiro(ncmAdicionadoData?.quantidadeAquisicaoTerceiros !== 0);
      setQuantidadeTotalTerceiros(ncmAdicionadoData?.quantidadeAquisicaoTerceiros.toString());
      setValorTotalTerceiros(formatCurrency(ncmAdicionadoData?.valorAquisicaoTerceiros));
    }
  }, [isEdicaoNcm, ncmAdicionadoData]);

  const receberInformacoesBaseCalculo = async () => {
    if (
      !validarVendaNacional(
        codigoNCMSelecionado,
        unidadeMedidaSelecionada,
        parseFloat(quantidade),
        receitaBruta,
        precoCorrente,
        tributosAdicionados,
        tributosPossiveisData,
        mostrarNotificacao,
        haCompraTerceiro,
        parseFloat(quantidadeTotalTerceiros),
        valorTotalTerceiros
      )
    )
      return false;
    setIsLoadingInfo(true);
    const ncmData: IInformacaoVendaNacionalEnviar = {
      quantidade: parseFloat(quantidade),
      valorReceitaBruta: removeFormatCurrency(receitaBruta),
      precoCorrente: removeFormatCurrency(precoCorrente),
      tributosValores: tributosAdicionados,
      totalCompraTerceiros: haCompraTerceiro ? removeFormatCurrency(valorTotalTerceiros) : 0,
    };
    await postReceberInformacoesVendaNacional(ncmData)
      .then((res) => {
        setInfo([
          {
            label: 'Preço Utilizado (R$)',
            value: res.precoUtilizado.valor,
            precoUtilizado:
              EPrecoUtilizadoVendaNacional[res.precoUtilizado.preco as keyof typeof EPrecoUtilizadoVendaNacional] + ' ',
          },
          { label: 'Deduções (R$)', value: res.deducoes },
          { label: 'Base de Cálculo (R$)', value: res.baseCalculo },
        ]);
        setIsLoadingInfo(false);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao calcular a Base de Cálculo. Por favor, tente novamente.',
          ...(err && { complementoSentryId: err }),
        });

        setIsLoadingInfo(false);
      });
  };

  return (
    <div>
      <div className={styles.title}>{isEdicaoNcm ? 'Edite NCM' : 'Adicione um NCM'}</div>
      {ncmAdicionadoDataError && isEdicaoNcm && <ErrorMessage errorMessage={''} error={ncmAdicionadoDataError} />}
      {isEdicaoNcm && ncmAdicionadoDataLoading ? (
        <BrLoading />
      ) : (
        <div className={styles.content}>
          <div className={styles.form}>
            <Row>
              <Col>
                <span className={styles.label}>Código NCM</span>
                {ncmLoading ? (
                  <BrLoading />
                ) : (
                  <BrSelect
                    data-testid='codigo-ncm'
                    key={codigoNCMSelecionado}
                    className={styles.select}
                    placeholder='Selecione um NCM...'
                    value={codigoNCMSelecionado}
                    onChange={(value: string) => {
                      setCodigoNCMSelecionado(value);
                    }}
                    options={
                      ncmData?.map((item: ICodigoNcm) => ({
                        label: `${item.codigo} - ${item.descricao}`,
                        value: item.codigo,
                      })) || []
                    }
                    disabled={false}
                  />
                )}
                {ncmError && (
                  <div className={styles.errorContainer}>
                    <span className={styles.errorText}>Não foi possível atualizar a lista de NCM</span>
                    <BrButton size='small' onClick={() => refetchNcm()}>
                      (Tentar novamente)
                    </BrButton>
                  </div>
                )}
              </Col>
              <Col>
                <span className={styles.label}>Unidade de medida</span>
                {unidadeMedidaLoading ? (
                  <BrLoading />
                ) : (
                  <BrSelect
                    data-testid='unidade-medida'
                    key={unidadeMedidaSelecionada}
                    className={styles.select}
                    placeholder='Selecione uma unidade de medida...'
                    value={unidadeMedidaSelecionada}
                    onChange={(value: string) => {
                      setUnidadeMedidaSelecionada(value);
                    }}
                    options={
                      unidadeMedidaData?.map((item) => {
                        return { label: item, value: item };
                      }) || []
                    }
                  />
                )}
                {unidadeMedidaError && (
                  <div className={styles.errorContainer}>
                    <span className={styles.errorText}>Não foi possível atualizar a lista de unidades de medida</span>
                    <BrButton size='small' onClick={() => refetchUnidadeMedida()}>
                      (Tentar novamente)
                    </BrButton>
                  </div>
                )}
              </Col>
              <Col>
                <span className={styles.label}>Quantidade Vendida</span>
                <BrInput
                  data-testid='quantidade-input'
                  autoComplete='off'
                  numeric={{
                    decimalScale: 4,
                    fixedDecimalScale: false,
                  }}
                  onChange={(e) => {
                    setQuantidade(e.target.value.replace(',', '.'));
                  }}
                  value={quantidade}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <span className={styles.label}>Receita Bruta de Venda (R$)</span>
                <BrInput
                  data-testid='receita-bruta-input'
                  autoComplete='off'
                  numeric={{
                    prefix: 'R$ ',
                    thousandSeparator: '.',
                    decimalScale: 2,
                    fixedDecimalScale: false,
                  }}
                  required
                  onChange={(e) => setReceitaBruta(e.target.value)}
                  value={receitaBruta}
                />
              </Col>
              <Col className={styles.dataContainer}>
                <span className={styles.label}>{EPrecoUtilizadoVendaNacional.PRECO_PRATICADO} (R$)</span>
                <span>
                  {receitaBruta && quantidade
                    ? formatCurrency(removeFormatCurrency(receitaBruta) / parseFloat(quantidade))
                    : '-'}
                </span>
              </Col>
              <Col>
                <span className={styles.label}>{EPrecoUtilizadoVendaNacional.PRECO_CORRENTE} (R$)</span>
                <BrInput
                  data-testid='preco-corrente-input'
                  autoComplete='off'
                  numeric={{
                    prefix: 'R$ ',
                    thousandSeparator: '.',
                    decimalScale: 11,
                    fixedDecimalScale: false,
                  }}
                  required
                  onChange={(e) => setPrecoCorrente(e.target.value)}
                  value={precoCorrente}
                />
              </Col>
            </Row>
          </div>
          {tributosPossiveisLoading ? (
            <BrLoading />
          ) : (
            <SecaoDeducoes
              key={codigoNCMSelecionado}
              deducoesPossiveis={tributosPossiveisData}
              deducoesAdicionadas={tributosAdicionados}
              setDeducoesAdicionadas={setTributosAdicionados}
              semMargem
              valorReceitaBruta={receitaBruta}
              isApresentarSecaoCompraTerceiros
              haCompraTerceiro={haCompraTerceiro}
              setHaCompraTerceiro={setHaCompraTerceiro}
              quantidadeCompraTerceiro={quantidadeTotalTerceiros}
              setQuantidadeCompraTerceiro={setQuantidadeTotalTerceiros}
              valorCompraTerceiro={valorTotalTerceiros}
              setValorCompraTerceiro={setValorTotalTerceiros}
            />
          )}
          {tributosPossiveisError && (
            <div className={styles.errorContainer}>
              <span className={styles.errorText}>Não foi possível atualizar a lista de tributos</span>
              <BrButton size='small' onClick={() => refetchTributosPossiveis()}>
                (Tentar novamente)
              </BrButton>
            </div>
          )}
          <div className={styles.atualizarInformacoesContainer}>
            <AtualizarInformacoes
              onClickFunction={() => receberInformacoesBaseCalculo()}
              info={info}
              isLoading={isLoadingInfo}
            />
          </div>
          <div className={styles.form}>
            <AnmSwitch
              label='O bem mineral é oriundo de rejeitos e estéreis de minerais associados utilizados em outras cadeias
                  produtivas?'
              value={haRejeito}
              setValue={setHaRejeito}
              tooltipText='Informação vinculada à redução da alíquota da CFEM em 50%, conforme § 7º, do Art. 6º, da Lei nº 7.990/1989. Os procedimentos para aproveitamento de rejeitos e estéreis estão dispostos na RESOLUÇÃO ANM Nº 85, DE 2 DE DEZEMBRO DE 2021.'
              switchDataTestId='switch-rejeito'
            />
            <AnmSwitch
              label='A substância possui uso específico?'
              value={haUsoEspecifico}
              setValue={setHaUsoEspecifico}
              tooltipText='Essa marcação só deve ser feita quando o bem mineral for destinado ao uso imediato na construção civil ou se enquadrar na categoria de rochas ornamentais, para fins de aplicação da alíquota de 1% (um por cento), nos termos do ANEXO da Lei nº 8.001/1990.'
              switchDataTestId='switch-uso-especifico'
            />
          </div>
          <div className={styles.buttonContainer}>
            {isEdicaoNcm === true && (
              <BrButton
                data-testid='btn-salvar-ncm'
                secondary
                size='small'
                icon='fas fa-save'
                onClick={() => {
                  adicionarNCM();
                }}
                loading={loadingBotao}
              >
                Salvar NCM
              </BrButton>
            )}
            {isEdicaoNcm === false && (
              <BrButton
                data-testid='btn-incluir-ncm'
                secondary
                size='small'
                icon='fas fa-plus'
                onClick={() => {
                  adicionarNCM();
                }}
                loading={loadingBotao}
              >
                Incluir NCM
              </BrButton>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormNCMVendaNacional;
